import React, { useState } from 'react';
import { FiSearch, FiX, FiBriefcase } from 'react-icons/fi';
import { useGetMyServices, type UserServiceItem } from '../../api/media';
import { formatServicePrice } from '../../utils/price';

interface ServicesModalProps {
  onSelect: (service: UserServiceItem) => void;
  onClose: () => void;
  selectedId?: number;
}

export const ServicesModal: React.FC<ServicesModalProps> = ({ onSelect, onClose, selectedId }) => {
  const [search, setSearch] = useState('');
  const { data: services = [], isLoading } = useGetMyServices();

  const filtered = services.filter((s) => s.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className="w-full sm:max-w-[540px] bg-[#0c0c0c] border border-white/10 rounded-[24px] sm:rounded-[40px] overflow-hidden shadow-2xl flex flex-col mx-4"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 sm:p-8 border-b border-white/5">
          <h3 className="text-white text-[20px] font-semibold">Services</h3>
          <button
            onClick={onClose}
            className="p-1 text-zinc-500 hover:text-white transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 flex-1 overflow-y-auto max-h-[70vh] sm:max-h-[60vh] no-scrollbar">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
            </div>
          ) : filtered.length === 0 && search === '' ? (
            /* Empty state — no services yet */
            <div className="flex flex-col items-center justify-center py-12 text-center gap-6">
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-zinc-600">
                <FiBriefcase size={32} />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-white text-xl font-semibold">No services added yet</p>
                <p className="text-zinc-500 max-w-[320px] leading-relaxed">
                  Add services to showcase what you offer and get booked faster.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="relative flex items-center bg-zinc-900/40 border border-white/5 rounded-2xl px-4 h-14 focus-within:border-blue-500/50 transition-all">
                <FiSearch size={20} className="text-blue-500 shrink-0" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search"
                  className="ml-3 bg-transparent border-none text-white text-[15px] font-normal placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
                />
              </div>

              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-zinc-600">
                    <FiSearch size={32} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="text-white text-xl font-semibold">No services found</p>
                    <p className="text-zinc-500 max-w-[320px] leading-relaxed">
                      Try a different keyword.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {filtered.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => onSelect(s)}
                      className={`w-full p-5 rounded-[24px] border transition-all text-left flex items-start gap-4 group ${
                        selectedId === s.id
                          ? 'bg-blue-600/10 border-blue-500/50'
                          : 'bg-zinc-900/40 border-white/5 hover:border-white/20 hover:bg-white/5'
                      }`}
                    >
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                          selectedId === s.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-zinc-800 text-zinc-500 group-hover:text-zinc-400'
                        }`}
                      >
                        <FiBriefcase size={24} />
                      </div>
                      <div className="flex-1 flex flex-col gap-1">
                        <h4
                          className={`font-semibold text-[17px] transition-colors ${selectedId === s.id ? 'text-blue-400' : 'text-white'}`}
                        >
                          {s.title}
                        </h4>
                        {s.description && (
                          <p className="text-zinc-500 text-[13px] leading-relaxed italic line-clamp-2">
                            {s.description}
                          </p>
                        )}
                        {s.price && (
                          <p className="text-white text-[14px] font-medium mt-1">
                            {formatServicePrice(s.price)}
                          </p>
                        )}
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full border-2 mt-1 shrink-0 transition-all flex items-center justify-center ${
                          selectedId === s.id ? 'border-green-500 bg-green-500' : 'border-zinc-700'
                        }`}
                      >
                        {selectedId === s.id && (
                          <div className="w-2.5 h-2.5 rounded-full bg-white" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
